/**
 * Modal Component
 * 
 * Custom modal window implementation without external dependencies
 * Uses Visual Viewport API for correct height on iOS Safari (dynamic toolbar)
 *
 * @package DesignSchool\Forms
 */

declare var jQuery: any;

export class Modal {
    private $: any;
    private $overlay: any;
    private $modal: any;
    private $body: any;
    private $html: any;
    private isOpenState: boolean = false;
    private modalClass: string = 'ds-modal';
    private overlayClass: string = 'ds-modal__overlay';
    private modalOpenClass: string = 'ds-modal-open';
    private vvResizeHandler: (() => void) | null = null;

    constructor() {
        this.$ = jQuery;
        this.$body = this.$('body');
        this.$html = this.$('html');
        this.init();
    }

    /**
     * Sync visual viewport height to CSS var for iOS Safari (fixes cut-off modal).
     * 100vh on iOS doesn't account for dynamic URL bar; visualViewport.height does.
     */
    private syncVisualViewportHeight(): void {
        const vv = (window as any).visualViewport;
        const h = vv ? vv.height : window.innerHeight;
        document.documentElement.style.setProperty('--ds-vvh', `${Math.round(h)}px`);
    }

    /**
     * Initialize modal structure
     */
    private init(): void {
        // Get or create modal container (БЭМ: ds-modal)
        this.$modal = this.$('.' + this.modalClass);
        
        if (!this.$modal.length) {
            // Create modal structure if it doesn't exist
            this.$modal = this.$('<div>')
                .addClass(this.modalClass)
                .appendTo(this.$body);
            
            // Create container inside modal
            const $container = this.$('<div>')
                .addClass('ds-modal__container')
                .appendTo(this.$modal);
            
            // Create close button with absolute positioning
            this.$('<button>')
                .addClass('ds-modal__close')
                .attr('type', 'button')
                .attr('aria-label', 'Закрыть')
                .appendTo($container);
            
            // Create body container
            this.$('<div>')
                .addClass('ds-modal__body')
                .appendTo($container);
        }

        // Create overlay (БЭМ: ds-modal__overlay)
        this.$overlay = this.$('.' + this.overlayClass);
        if (!this.$overlay.length) {
            this.$overlay = this.$('<div>')
                .addClass(this.overlayClass)
                .appendTo(this.$body);
        }

        // Bind close handlers
        this.bindCloseHandlers();

        // iOS Safari: keep --ds-vvh in sync with visual viewport (fixes modal cut-off)
        this.syncVisualViewportHeight();
        const vv = (window as any).visualViewport;
        if (vv) {
            this.vvResizeHandler = () => this.syncVisualViewportHeight();
            vv.addEventListener('resize', this.vvResizeHandler);
            vv.addEventListener('scroll', this.vvResizeHandler);
        }
    }

    /**
     * Bind close event handlers
     */
    private bindCloseHandlers(): void {
        // Close on overlay click
        this.$overlay.on('click', (e: Event) => {
            if (e.target === this.$overlay[0]) {
                this.close();
            }
        });

        // Close when clicking outside the modal content (on the modal backdrop)
        this.$modal.on('click', (e: Event) => {
            if (e.target === this.$modal[0]) {
                this.close();
            }
        });

        // Close on ESC key
        this.$(document).on('keydown.modal', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isOpenState) {
                this.close();
            }
        });

        // Close button (if exists in modal) (БЭМ: ds-modal__close)
        this.$modal.on('click', '.ds-modal__close', () => {
            this.close();
        });
    }

    /**
     * Open modal with content
     * 
     * @param content HTML content or selector
     */
    public open(content?: string): void {
        if (this.isOpenState) {
            return;
        }

        // Set content if provided
        if (content) {
            this.setContent(content);
        }

        // Show overlay and modal (БЭМ модификаторы)
        this.$overlay.addClass('ds-modal__overlay--active');
        this.$modal.addClass('ds-modal--active');
        this.$body.addClass(this.modalOpenClass);
        this.$html.addClass(this.modalOpenClass);

        // Re-sync visual viewport for mobile (iOS Safari)
        this.syncVisualViewportHeight();

        // Prevent page scroll (both html and body for cross-browser/theme compatibility)
        this.$html.css('overflow', 'hidden');
        this.$body.css('overflow', 'hidden');

        this.isOpenState = true;

        // Trigger event
        this.$(document).trigger('ds-forms-modal-opened', [this.$modal]);
    }

    /**
     * Close modal
     */
    public close(): void {
        if (!this.isOpenState) {
            return;
        }

        // Clear iframe modifier when closing
        this.clearIframeModifier();

        // Hide overlay and modal (БЭМ модификаторы)
        this.$overlay.removeClass('ds-modal__overlay--active');
        this.$modal.removeClass('ds-modal--active');
        this.$body.removeClass(this.modalOpenClass);
        this.$html.removeClass(this.modalOpenClass);

        // Restore page scroll
        this.$html.css('overflow', '');
        this.$body.css('overflow', '');

        this.isOpenState = false;

        // Trigger event
        this.$(document).trigger('ds-forms-modal-closed', [this.$modal]);
    }

    /**
     * Open modal with iframe content (e.g. external form)
     *
     * @param url iframe src URL
     * @param options optional: minHeight, title
     */
    public openIframe(url: string, options?: { minHeight?: number; title?: string }): void {
        const minHeight = options?.minHeight ?? 500;
        const title = options?.title ?? '';
        // div, а не h3: заголовок модального окна не относится к структуре контента страницы
        const titleHtml = title ? `<div class="ds-modal__iframe-title">${title}</div>` : '';
        const content = `${titleHtml}<div class="ds-modal__iframe-wrapper" style="min-height: ${minHeight}px;"><iframe src="${url}" class="ds-modal__iframe" frameborder="0"></iframe></div>`;
        this.setContent(content);
        this.getModal().addClass('ds-modal--iframe');
        this.open();
    }

    /**
     * Clear iframe modifier when closing (called internally or before setContent)
     */
    public clearIframeModifier(): void {
        this.getModal().removeClass('ds-modal--iframe');
    }

    /**
     * Set modal content
     * 
     * @param content HTML content
     */
    public setContent(content: string): void {
        // Set content in modal body (БЭМ: ds-modal__body)
        const $body = this.$modal.find('.ds-modal__body');
        if ($body.length) {
            $body.html(content);
        } else {
            // Fallback: create structure if missing
            const $container = this.$modal.find('.ds-modal__container');
            if ($container.length) {
                $container.append('<div class="ds-modal__body">' + content + '</div>');
            } else {
                this.$modal.html('<div class="ds-modal__container"><div class="ds-modal__body">' + content + '</div></div>');
            }
        }
    }

    /**
     * Get modal element
     */
    public getModal(): any {
        return this.$modal;
    }

    /**
     * Check if modal is open
     */
    public isOpen(): boolean {
        return this.isOpenState;
    }

    /**
     * Destroy modal (cleanup)
     */
    public destroy(): void {
        this.$(document).off('keydown.modal');
        const vv = (window as any).visualViewport;
        if (vv && this.vvResizeHandler) {
            vv.removeEventListener('resize', this.vvResizeHandler);
            vv.removeEventListener('scroll', this.vvResizeHandler);
        }
        this.close();
    }
}

// Export singleton
export const modal = new Modal();

